import React from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouteProps,
} from 'react-router-dom';

const RouteWithBreadcrumb = Route as React.ComponentType<RouteProps & { breadcrumb?: string }>;
import OnboardingSteps from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingSteps';
import ResetPassword from 'src/components/ResetPassword/ResetPassword';
import EmailVerify from 'src/components/EmailVerify/EmailVerify';
import UninstallExtension from 'src/components/UninstallExtension/UninstallExtension';
import { RequireAuth } from 'src/provider/AuthProvider';
import WelcomePage from 'src/pages/WelcomePage/WelcomePage';
import DashboardPage from 'src/pages/DashboardPage/DashboardPage';
import HomePage from 'src/pages/HomePage/HomePage';
import AccountPage from 'src/pages/AccountPage/AccountPage';
import ProjectPage from 'src/pages/ProjectPage/ProjectPage';
import { CreationFlowWrapper } from 'src/components/CreationFlowWrapper/CreationFlowWrapper';
import ControlDeskPage from 'src/pages/ControlDeskPage/ControlDeskPage';
import AnalyticsPage from 'src/pages/AnalyticsPage/AnalyticsPage';
import IntegrationsPage from 'src/pages/IntegrationsPage/IntegrationsPage';
import ConversationsPage from 'src/pages/ConversationsPage/ConversationsPage';
import ConfigurationPage from 'src/pages/ConfigurationPage/ConfigurationPage';
import CreateProjectPage from 'src/pages/ProjectPage/CreateProject/CreateProjectPage';
import SubscriptionPage from 'src/pages/SubscriptionPage/SubscriptionPage';
import AIKnowledgeBasePage from 'src/pages/AIKnowledgeBasePage/AIKnowledgeBasePage';
import { KnowledgeBaseProvider } from 'src/provider/KnowledgeBaseProvider';
import { CurrentKnowledgeBasePage } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBasePage';
import OnboardingPage from 'src/pages/OnboardingPage/OnboardingPage';
import AuthPage from 'src/pages/AuthPage/AuthPage';
import RegisterPage from 'src/pages/RegisterPage/RegisterPage';
import Main from 'src/components/Main/Main';
import { ROUTE_PATHS } from 'src/utils/route-paths';
import { CreateProjectSteps } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectSteps';
import RegisterEmailPage from 'src/pages/RegisterPage/RegisterEmailPage/RegisterEmailPage';
import RegisterGooglePage from 'src/pages/RegisterPage/RegisterGooglePage/RegisterGooglePage';
import { TrackPageView } from 'src/components/TrackPageView/TrackPageView';
import { MainErrorElement } from 'src/components/MainErrorElement/MainErrorElement';
import { accountLoader } from 'src/utils/accountLoader';
import { ConfigurationProvider } from 'src/provider/ConfigurationProvider';

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path="/"
      element={<TrackPageView />}
      errorElement={<MainErrorElement />}
      loader={accountLoader}
    >
      <Route path={ROUTE_PATHS.RESET_PASSWORD} element={<ResetPassword />} />
      <Route path={ROUTE_PATHS.EMAIL_VERIFY} element={<EmailVerify />} />
      <Route path={ROUTE_PATHS.UNINSTALL_EXTENSION} element={<UninstallExtension />} />
      <Route
        path={ROUTE_PATHS.WELCOME}
        element={
          <RequireAuth redirect={ROUTE_PATHS.AUTH}>
            <WelcomePage />
          </RequireAuth>
        }
      />
      <RouteWithBreadcrumb
        path={ROUTE_PATHS.DASHBOARD}
        breadcrumb={'Menu'}
        element={
          <RequireAuth redirect={ROUTE_PATHS.AUTH}>
            <DashboardPage />
          </RequireAuth>
        }
      >
        <Route
          path={ROUTE_PATHS.HOME}
          element={
            <RequireAuth redirect={ROUTE_PATHS.AUTH}>
              <HomePage />
            </RequireAuth>
          }
        />
        <RouteWithBreadcrumb
          path={ROUTE_PATHS.ACCOUNT}
          breadcrumb={'Account'}
          element={
            <RequireAuth redirect={ROUTE_PATHS.AUTH}>
              <AccountPage />
            </RequireAuth>
          }
        />
        <Route path={ROUTE_PATHS.PROJECTS} element={<Outlet />}>
          <Route path={ROUTE_PATHS.DASHBOARD_PROJECT} element={<ProjectPage />} />
          <Route
            path={ROUTE_PATHS.DASHBOARD_PROJECT_CONTROL_DESK}
            element={
              <RequireAuth redirect={ROUTE_PATHS.AUTH}>
                <CreationFlowWrapper>
                  <ControlDeskPage />
                </CreationFlowWrapper>
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_PATHS.DASHBOARD_PROJECT_ANALYTICS}
            element={
              <RequireAuth redirect={ROUTE_PATHS.AUTH}>
                <AnalyticsPage />
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_PATHS.DASHBOARD_PROJECT_INTEGRATIONS}
            element={
              <RequireAuth redirect={ROUTE_PATHS.AUTH}>
                <IntegrationsPage />
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_PATHS.DASHBOARD_PROJECT_CONVERSATIONS}
            element={
              <RequireAuth redirect={ROUTE_PATHS.AUTH}>
                <ConversationsPage />
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_PATHS.DASHBOARD_PROJECT_CONFIGURATION}
            element={
              <RequireAuth redirect={ROUTE_PATHS.AUTH}>
                <ConfigurationProvider>
                  <ConfigurationPage />
                </ConfigurationProvider>
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_PATHS.DASHBOARD_PROJECT_NEW}
            element={
              <RequireAuth redirect={ROUTE_PATHS.AUTH}>
                <CreateProjectPage />
              </RequireAuth>
            }
          />
          {[
            ROUTE_PATHS.DASHBOARD_PROJECT_NEW_STEP,
            ROUTE_PATHS.DASHBOARD_PROJECT_NEW_STEP_EDIT,
          ].map((path, index) => (
            <Route
              path={path}
              key={`route-${index}`}
              element={
                <RequireAuth redirect={ROUTE_PATHS.AUTH}>
                  <ConfigurationProvider>
                    <CreateProjectSteps />
                  </ConfigurationProvider>
                </RequireAuth>
              }
            />
          ))}
        </Route>
        <Route
          path={ROUTE_PATHS.SUBSCRIPTION}
          element={
            <RequireAuth redirect={ROUTE_PATHS.AUTH}>
              <SubscriptionPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTE_PATHS.AI_KNOWLEDGE_BASE}
          element={
            <RequireAuth redirect={ROUTE_PATHS.AUTH}>
              <AIKnowledgeBasePage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTE_PATHS.CURRENT_AI_KNOWLEDGE_BASE}
          element={
            <RequireAuth redirect={ROUTE_PATHS.AUTH}>
              <KnowledgeBaseProvider>
                <CurrentKnowledgeBasePage />
              </KnowledgeBaseProvider>
            </RequireAuth>
          }
        />
      </RouteWithBreadcrumb>
      <Route
        path={ROUTE_PATHS.ONBOARDING}
        element={
          <RequireAuth redirect={ROUTE_PATHS.AUTH}>
            <OnboardingPage />
          </RequireAuth>
        }
      >
        <Route
          path={ROUTE_PATHS.ONBOARDING_STEPS}
          element={
            <RequireAuth redirect={ROUTE_PATHS.AUTH}>
              <OnboardingSteps />
            </RequireAuth>
          }
        />
      </Route>
      <Route path={ROUTE_PATHS.AUTH} element={<AuthPage />} />
      <Route path={ROUTE_PATHS.REGISTER_OLD} element={<Navigate to={ROUTE_PATHS.SIGN_UP} />} />
      <Route path={ROUTE_PATHS.SIGN_UP} element={<RegisterPage />} />
      <Route path={ROUTE_PATHS.SIGN_UP_EMAIL} element={<RegisterEmailPage />} />
      <Route path={ROUTE_PATHS.SIGN_UP_GOOGLE} element={<RegisterGooglePage />} />
      <Route path={ROUTE_PATHS.MAIN} element={<Main />} />
      <Route
        path="*"
        element={
          <RequireAuth redirect={ROUTE_PATHS.AUTH}>
            <Navigate to={ROUTE_PATHS.HOME} />
          </RequireAuth>
        }
      />
    </Route>,
  ),
);
